// Собственная анонимная статистика посещений. Без имён, почты и IP: посетитель — случайный код
// в этом браузере, визит — серия открытий без пауз дольше 30 минут. Серверная часть — backend/analytics_routes.py.
// Уважает сигнал «Не отслеживать» (Do Not Track) и не считает страницы админки.
import { API_URL } from "./config";

const VISITOR_KEY = "sv_vid";
const SESSION_KEY = "sv_ses";
const SESSION_GAP_MS = 30 * 60 * 1000;
const IDLE_MS = 60 * 1000;      // без действий дольше минуты время не считается «активным»
const PING_EVERY_S = 15;

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, "") : (Date.now().toString(36) + Math.random().toString(36).slice(2, 14)).padEnd(16, "0"));

function store(key, value) {
  try {
    if (value === undefined) return JSON.parse(window.localStorage.getItem(key));
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch { /* приватный режим: статистика просто не пишется */ }
  return null;
}

let started = false;
let hit = null;                  // { id, active, scroll, sent }
let lastAction = Date.now();
let firstViewOfPage = true;      // document.referrer верен только для первого открытия после загрузки страницы

const disabled = () => {
  const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
  return dnt === "1" || dnt === "yes";
};

function visitorId() {
  let id = store(VISITOR_KEY);
  if (!id || typeof id !== "string") { id = newId(); store(VISITOR_KEY, id); }
  return id;
}

function sessionId() {
  const now = Date.now();
  let s = store(SESSION_KEY);
  if (!s || !s.id || now - (s.last || 0) > SESSION_GAP_MS) s = { id: newId(), last: now };
  s.last = now;
  store(SESSION_KEY, s);
  return s.id;
}

function send(payload) {
  const url = `${API_URL}/t`;
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  } catch { /* статистика не должна ничего ломать */ }
}

function scrollPercent() {
  const el = document.documentElement;
  const total = Math.max(el.scrollHeight, 1);
  return Math.min(100, Math.round(((window.scrollY || el.scrollTop) + window.innerHeight) * 100 / total));
}

function flush() {
  if (!hit) return;
  send({ t: "ping", h: hit.id, a: Math.round(hit.active), sc: hit.scroll });
}

function onAction() { lastAction = Date.now(); }

function tick() {
  if (!hit || document.visibilityState !== "visible") return;
  if (Date.now() - lastAction <= IDLE_MS) hit.active += 1;
  hit.scroll = Math.max(hit.scroll, scrollPercent());
  if (hit.active > 0 && Math.round(hit.active) % PING_EVERY_S === 0 && hit.sentAt !== Math.round(hit.active)) {
    hit.sentAt = Math.round(hit.active);
    flush();
  }
}

function startListeners() {
  if (started) return;
  started = true;
  ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach(e =>
    window.addEventListener(e, onAction, { passive: true, capture: true }));
  window.setInterval(tick, 1000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
  window.addEventListener("pagehide", flush);
}

const LAST_KEY = "sv_last";
const RELOAD_WINDOW_MS = 15 * 1000;

// Приложение само перезагружает страницу, когда подключается service worker (первый визит, обновления).
// Такая перезагрузка той же страницы — не новое открытие: продолжаем прежнее.
function isQuickReload(pathname) {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    const last = JSON.parse(window.sessionStorage.getItem(LAST_KEY) || "null");
    return !!(nav && nav.type === "reload" && last && last.p === pathname && Date.now() - last.t < RELOAD_WINDOW_MS && last.h);
  } catch { return false; }
}

function rememberHit(pathname, id) {
  try { window.sessionStorage.setItem(LAST_KEY, JSON.stringify({ p: pathname, t: Date.now(), h: id })); } catch { /* без хранилища — не страшно */ }
}

/** Вызывается при каждом открытии страницы (в том числе при переходах внутри приложения). */
export function trackPageview(pathname, search = "") {
  if (disabled() || !pathname || pathname.startsWith("/admin")) { hit = null; return; }
  startListeners();
  onAction();
  if (isQuickReload(pathname)) {
    const last = JSON.parse(window.sessionStorage.getItem(LAST_KEY));
    hit = { id: last.h, active: 0, scroll: scrollPercent(), sentAt: 0 };
    firstViewOfPage = false;
    return;
  }
  flush();   // закрываем предыдущую страницу
  const utm = new URLSearchParams(search).get("utm_source") || undefined;
  hit = { id: newId(), active: 0, scroll: scrollPercent(), sentAt: 0 };
  send({
    t: "pv", h: hit.id, v: visitorId(), s: sessionId(), p: pathname,
    r: firstViewOfPage ? document.referrer || undefined : undefined, u: utm, w: window.innerWidth,
  });
  rememberHit(pathname, hit.id);
  firstViewOfPage = false;
}
